import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logger from "../utils/logger";
import { Search, X, Loader2, AlertTriangle, CheckCircle } from "lucide-react";

const API = `${window.location.origin}/api`;

export function QuickScreenButton() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [screening, setScreening] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const handleScreen = async () => {
    if (!name.trim()) return;
    setScreening(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/screening/run-quick`, {
        name: name.trim(),
        checks: ["sanctions", "pep", "adverse_media"],
      }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      logger.error("Quick screen failed:", err);
      setResult({ error: true });
    } finally {
      setScreening(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setName("");
    setResult(null);
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="quick-screen-fab"
          style={{
            position: "fixed", bottom: "24px", right: "24px", width: "52px", height: "52px",
            borderRadius: "50%", background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            border: "none", cursor: "pointer", boxShadow: "0 8px 24px rgba(37,99,235,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
            transition: "transform 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
        >
          <Search style={{ width: "22px", height: "22px", color: "#fff" }} />
        </button>
      )}

      {/* Quick Screen Panel */}
      {open && (
        <div data-testid="quick-screen-panel" style={{
          position: "fixed", bottom: "24px", right: "24px", width: "340px",
          background: "#0d1117", border: "1px solid #1e2530", borderRadius: "14px",
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)", zIndex: 50, overflow: "hidden",
        }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #1e2530", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="flex items-center gap-2">
              <Search style={{ width: "14px", height: "14px", color: "#2563eb" }} />
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#f1f5f9" }}>Quick Screen</span>
            </div>
            <button onClick={handleClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#475569" }}>
              <X style={{ width: "14px", height: "14px" }} />
            </button>
          </div>

          <div style={{ padding: "14px 16px" }}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleScreen()}
              placeholder="Enter name to screen..."
              data-testid="quick-screen-input"
              autoFocus
              style={{
                width: "100%", background: "#080c12", border: "1px solid #1e2530",
                borderRadius: "8px", padding: "10px 12px", color: "#f1f5f9",
                fontSize: "13px", outline: "none", boxSizing: "border-box", marginBottom: "10px",
              }}
            />
            <button onClick={handleScreen} disabled={screening || !name.trim()} data-testid="quick-screen-go-btn" style={{
              width: "100%", background: screening ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", padding: "9px", cursor: screening ? "not-allowed" : "pointer",
              color: "#fff", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            }}>
              {screening ? <><Loader2 className="w-3 h-3 animate-spin" /> Screening...</> : "Screen Now"}
            </button>

            {result && !result.error && (
              <div data-testid="quick-screen-result" style={{ marginTop: "12px", padding: "10px", borderRadius: "8px", background: "#080c12", border: "1px solid #1e2530" }}>
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#f1f5f9" }}>
                    Score: <span style={{ color: (result.riskScore || result.risk_score || 0) > 50 ? "#ef4444" : "#10b981" }}>{result.riskScore || result.risk_score || 0}</span>
                  </span>
                  {(result.riskLevel === "HIGH" || result.status === "flagged")
                    ? <AlertTriangle style={{ width: "14px", height: "14px", color: "#ef4444" }} />
                    : <CheckCircle style={{ width: "14px", height: "14px", color: "#10b981" }} />
                  }
                </div>
                <div style={{ fontSize: "10px", color: "#475569" }}>
                  {(result.checks_requested || result.results || []).map(r => typeof r === 'string' ? r : r.check).join(", ")} checked
                </div>
                <button onClick={() => { handleClose(); navigate("/screening"); }} style={{
                  marginTop: "8px", width: "100%", background: "transparent", border: "1px solid #1e2530",
                  borderRadius: "6px", padding: "6px", cursor: "pointer", color: "#60a5fa", fontSize: "11px",
                }}>
                  View Full Results
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
